import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SiteMeasurementDetailsComponent } from './details/site-measurement-details.component';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { ReportsService } from 'app/modules/admin/transactions/inwards/reports.service';
import { AlertMessageService } from 'app/core/common/alert.message.service';
import { BaseRespone } from 'app/core/models/common.response';
import { Observable, of, Subject } from 'rxjs';
import { SiteMeasurementsService } from './site-measurement.service';
import { SiteMeasurement } from './site-measurement.types';
import { debounceTime, map, startWith, take, takeUntil } from 'rxjs/operators';
import { InwardReport } from 'app/modules/admin/transactions/inwards/reports.types';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { LookupService } from 'app/core/common/services/lookup.service';
import { CommonDropDown } from 'app/core/common/types/dropdown.types';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'app-site-measurement',
    templateUrl: './site-measurement.component.html',
    styleUrls: ['./site-measurement.component.scss'],
})
export class SiteMeasurementComponent implements OnInit {
    @Output('calculationAreas') calculationAreas: EventEmitter<any> =
        new EventEmitter();
    selection = new SelectionModel<SiteMeasurement>(true, []);
    @Input() report: InwardReport;
    @Input() reportId: number;
    @Input() reportType: string;

    totalArea: number = 0.0;
    totalBuiltUp: number = 0.0;
    totalSuperBuiltUp: number = 0.0;

    selectedSiteMeasurement: SiteMeasurement;
    selectedIds: number[] = [];
    isAllSelectedCheckBox: boolean = false;
    siteMeasurements$: Observable<SiteMeasurement[]>;
    options: CommonDropDown[] = [];
    filteredOptions: Observable<CommonDropDown[]>;
    siteMeasurementForm: FormGroup;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    constructor(
        private _matDialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
        private _siteMeasurementService: SiteMeasurementsService,
        private _alertMessageService: AlertMessageService,
        private _lookupService: LookupService,
        private _formBuilder: FormBuilder,
        private _cdr : ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.filteredOptions = of(this.options);
        this._siteMeasurementService
            .getLandSiteMeasurements(this.reportId)
            .subscribe((res) => {
                this.calculateSiteMeasurementsTotal();
            });

        this.siteMeasurements$ =
            this._siteMeasurementService.siteMeasurements$.pipe(
                map((data) => {
                    data?.sort((a, b) => a.orderIndex - b.orderIndex);
                    return data;
                })
            );

        // Get the contact
        this._siteMeasurementService.siteMeasurement$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((contact: SiteMeasurement) => {
                // Update the selected contact
                this.selectedSiteMeasurement = contact;

                // Mark for check
                //this._changeDetectorRef.markForCheck();
            });

        this.siteMeasurementForm = this._formBuilder.group({
            id: [''],
            reportId: [this.reportId],
            type: ['', [Validators.required]],
            particular: [''],
            width: [0],
            depth: [0],
            total: [0],
        });

        this._lookupService
            .getOptionByTypeId(91, this.reportType)
            .subscribe((res) => {
                this.options = res;
                this.filteredOptions = this.siteMeasurementForm
                    .get('particular')
                    .valueChanges.pipe(
                        startWith(''),
                        map((value) => this._optionsfilter(value || ''))
                    );
            });
    }

    displayOptionsFn(option: any): string {
        return option && option.name ? option.name : '';
    }

    particularValueChange(event) {
        if (event) {
            this.filteredOptions = of(this._optionsfilter(event));
        } else {
            this.filteredOptions = of(this.options);
        }
    }

    private _optionsfilter(name: string): CommonDropDown[] {
        const filterValue = name.toString().toLowerCase();

        return this.options.filter((option) =>
            option.name.toLowerCase().includes(filterValue)
        );
    }

    new(selectedValue?: string): void {
        this.siteMeasurements$ = this.siteMeasurements$.pipe(
            map((measurements) => [
                ...measurements,
                {
                    id: 0,
                    reportId: this.reportId,
                    type: selectedValue,
                    particular: '',
                    width: 0,
                    depth: 0,
                    total: 0,
                    siteArea: 0,
                    siteBuiltUp: 0,
                    siteSuperBuiltUp: 0,
                    orderIndex: measurements.length + 1,
                    selected: false,
                    isEditing: true,
                },
            ])
        );
    }

    cancelNewRow(index: number) {
        this.siteMeasurements$
            .pipe(
                map((measurements) => {
                    if (!measurements[index].particular?.trim()) {
                        return measurements.filter((_, i) => i !== index);
                    } else {
                        measurements[index].isEditing = false;
                        return measurements;
                    }
                })
            )
            .subscribe((updatedMeasurements) => {
                this.siteMeasurements$ = of(updatedMeasurements); // Update the observable
            });
    }

    toggleEdit(index: number) {
        this.siteMeasurements$ = this.siteMeasurements$.pipe(
            map((measurements) =>
                measurements.map((item, i) =>
                    i === index ? { ...item, isEditing: !item.isEditing } : item
                )
            )
        );
    }

    save(sitemeasurement: SiteMeasurement): void {
        if (
            !sitemeasurement.particular ||
            sitemeasurement.particular.trim() === ''
        ) {
            this._alertMessageService.error('Please select particular');
            return;
        }
        if (sitemeasurement.id !== null && sitemeasurement.id !== 0) {
            this._siteMeasurementService
                .updateSiteMeasurement(sitemeasurement)
                .subscribe((response) => {
                    if (response.isSuccess) {
                        // Show a success message...
                        this._alertMessageService.success(
                            'Document Measurement updated successfully'
                        );
                        this._cdr.detectChanges();
                    } else {
                        this._alertMessageService.error(
                            response.errors.join('\n')
                        );
                    }
                });
        } else {
            this._siteMeasurementService
                .createSiteMeasurement(sitemeasurement)
                .subscribe((response) => {
                    if (response.isSuccess) {
                        this._alertMessageService.success(
                            'Site measurement added successfully'
                        );
                    } else {
                        this._alertMessageService.error(
                            response.errors.join('\n')
                        );
                    }
                });
        }
    }

    calculateTotal(measurement: SiteMeasurement): void {
        if (measurement.width && measurement.depth) {
            measurement.total = Number(
                (measurement.width * measurement.depth).toFixed(2)
            );
        } else {
            measurement.total = 0;
        }
    }

    /**
     * Delete the selected product using the form data
     */
    delete(id: number): void {
        // Open the confirmation dialog
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete Site Measurement',
            message:
                'Are you sure you want to remove? This action cannot be undone!',
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        // Subscribe to the confirmation dialog closed action
        confirmation.afterClosed().subscribe((result) => {
            // If the confirm button pressed...
            if (result === 'confirmed') {
                //Delete the product on the server
                this._siteMeasurementService
                    .deleteSiteMeasurement(id)
                    .subscribe((res) => {
                        if (res.isSuccess) {
                            this._alertMessageService.success(
                                'Site measurement deleted successfully'
                            );
                        } else {
                            this._alertMessageService.error(
                                res.errors.join('\n')
                            );
                        }
                    });
            }
        });
    }
    async deleteSelectedRows() {
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete All Site Measurement',
            message:
                'Are you sure you want to remove? This action cannot be undone!',
            actions: {
                confirm: {
                    label: 'Delete',
                },
            },
        });

        const result = await confirmation.afterClosed().toPromise();

        if (result === 'confirmed') {
            const ids = this.selection.selected.map((item) => item.id);
            const res = await this._siteMeasurementService
                .deleteAllSiteMeasurement(ids)
                .toPromise();

            if (res.isSuccess) {
                this._alertMessageService.success(
                    'Site measurement all deleted successfully'
                );
                this.selection.clear();
                this.siteMeasurements$.subscribe();
            } else {
                this._alertMessageService.error(res.errors.join('\n'));
            }
        }
    }
    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
    calculateSiteMeasurementsTotal(): void {
        this._siteMeasurementService.siteMeasurements$.subscribe((res) => {
            this.report.siteArea = Number(
                res
                    .filter(
                        (a) =>
                            a.type === 'Land Area' || a.type === 'Carpet Area'
                    )
                    .reduce((sum, current) => sum + current.total, 0)
                    .toFixed(2)
            );

            this.report.siteBuiltUp = Number(
                res
                    .filter(
                        (a) =>
                            a.type !== 'Land Area' && a.type !== 'Carpet Area'
                    )
                    .reduce((sum, current) => sum + current.total, 0)
                    .toFixed(2)
            );

            this.calculationAreas.emit();
        });
    }

    updateCheckedList(event, row) {
        this.siteMeasurements$.pipe(take(1)).subscribe((measurements) => {
            measurements.forEach((item) => {
                if (item.id == row.id) {
                    item.selected = event.checked;
                    if (item.selected) {
                        this.selection.select(item); // Select item in selection model
                    } else {
                        this.selection.deselect(item);
                    }
                }
            });

            this.isAllSelectedCheckBox = !measurements.some((a) => !a.selected);
        });
    }

    selectAll(event: any) {
        const checked = event.checked;
        this.siteMeasurements$
            .pipe(take(1))
            .subscribe((measurements: any[]) => {
                measurements.forEach((measurement) => {
                    measurement.selected = checked;
                });
            });
    }
    checkboxLabel() {
        if (this.isAllSelected()) {
            return 'Deselect all';
        }
        if (this.selection.isEmpty()) {
            return 'Select all';
        }
        return `${this.selection.selected.length} selected`;
    }

    isAllSelected() {
        // Check if all measurements are selected
        return this.siteMeasurements$.pipe(
            take(1),
            map((measurements) =>
                measurements.every((measurement) => measurement.selected)
            )
        );
    }

    toggleAllRows() {
        this.isAllSelected().subscribe((allSelected) => {
            if (!allSelected) {
                // Select all items
                this.siteMeasurements$
                    .pipe(take(1))
                    .subscribe((measurements) => {
                        measurements.forEach((item) => {
                            item.selected = true;
                            this.selection.select(item); // Select item in selection model
                        });
                    });
                this.isAllSelectedCheckBox = true;
            } else {
                // Deselect all items
                this.siteMeasurements$
                    .pipe(take(1))
                    .subscribe((measurements) => {
                        measurements.forEach((item) => {
                            item.selected = false;
                            this.selection.deselect(item); // Deselect item in selection model
                        });
                    });
                this.isAllSelectedCheckBox = false;
            }
        });
    }

    drop(event: CdkDragDrop<SiteMeasurement[]>): void {
        // Retrieve current data from the observable
        this.siteMeasurements$.pipe(take(1)).subscribe((data) => {
            if (data) {
                // Reorder locally
                moveItemInArray(data, event.previousIndex, event.currentIndex);

                // Update orderIndex
                data.forEach((item, index) => {
                    item.orderIndex = index + 1; // Reset order index
                    this._siteMeasurementService
                        .updateSiteMeasurement(item)
                        .subscribe();
                });

                // Update in the service
                //this._documentMeasurementService.updateDocumentMeasurements(data);
            }
        });
    }
}
